import db from "../config/firebase.config";
import firebase from "firebase/compat/app";
import { pushNotification } from "./communities";

// Super administrators — the only accounts that can grant/revoke verification.
// The default development account is scrubsmedina.
export const SUPER_ADMIN_EMAILS = ["scrubsmedina@gmail.com"];

// These emails get verified on dev sign-in with the chosen badge type.
export const BOOTSTRAP_VERIFIED_EMAILS = ["scrubsmedina@gmail.com"];

const VERIFIED_COLL = "verifiedUsers";

// ─── Profile + default account seeding ───────────────────────────────────────
// Ensures a users/{uid} profile exists (seeding the SUPER_ADMIN role for the
// default account) and seeds the blue verification doc for the default account.
// Safe to call on every login — only writes when the doc is missing/outdated.
export const ensureUserProfile = async (user) => {
    if (!user) return;
    const email = (user.email || "").toLowerCase();
    const isAdmin = SUPER_ADMIN_EMAILS.includes(email);

    // Ensure the user profile doc exists.
    const profileRef = db.collection("users").doc(user.uid);
    try {
        const existing = await profileRef.get();
        if (!existing.exists) {
            await profileRef.set({
                uid: user.uid,
                email,
                displayName: user.displayName || email,
                photoURL: user.photoURL || "",
                username: email.split("@")[0],
                bio: "",
                role: isAdmin ? "SUPER_ADMIN" : "MEMBER",
                createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            });
        } else if (isAdmin && existing.data()?.role !== "SUPER_ADMIN") {
            await profileRef.update({ role: "SUPER_ADMIN" });
        }
    } catch {}

    // Seed the verified doc for the default account.
    if (BOOTSTRAP_VERIFIED_EMAILS.includes(email)) {
        const doc = db.collection(VERIFIED_COLL).doc(user.uid);
        try {
            const existing = await doc.get();
            if (!existing.exists || !existing.data()?.verified) {
                await doc.set({
                    uid: user.uid,
                    name: user.displayName || email,
                    email,
                    verified: true,
                    verificationType: "blue",
                    verifiedBy: "scrubsmedina",
                    verifiedDate: new Date().toISOString(),
                });
            }
        } catch {}
    }
};

// Keep legacy names working (Navbar / older call sites).
export const ensureDefaultVerifiedUser = ensureUserProfile;
export const ensureVerifiedByEmail = ensureUserProfile;

// Subscribe to a single user profile doc. Returns an unsubscribe fn.
export const subscribeUserProfile = (uid, cb) => {
    if (!uid) {
        cb(null);
        return () => {};
    }
    return db.collection("users").doc(uid).onSnapshot(
        (doc) => cb(doc.exists ? doc.data() : null),
        () => cb(null)
    );
};

// Subscribe to verification documents. Returns { uid -> doc }.
export const subscribeVerifiedUsers = (cb) =>
    db.collection(VERIFIED_COLL).onSnapshot(
        (snap) => {
            const map = {};
            snap.forEach((d) => (map[d.id] = { id: d.id, ...d.data() }));
            cb(map);
        },
        () => cb({})
    );

// Legacy boolean check (still used by old call sites).
export const isVerified = (verifiedMap, uid, email) => {
    if (uid && verifiedMap[uid] && verifiedMap[uid].verified) return true;
    if (email && BOOTSTRAP_VERIFIED_EMAILS.includes(email.toLowerCase())) return true;
    return false;
};

// Returns the global badge type for a user: "blue" | "gold" | null.
// Purple is a founder badge computed per-community from community.founderUid.
export const getBadge = (verifiedMap, uid, email, community) => {
    const founderUid = community?.founderUid;
    if (founderUid && uid === founderUid) return "purple";
    if (uid && verifiedMap[uid] && verifiedMap[uid].verified) {
        return verifiedMap[uid].verificationType === "gold" ? "gold" : "blue";
    }
    if (email && BOOTSTRAP_VERIFIED_EMAILS.includes(email.toLowerCase())) return "blue";
    return null;
};

// Badge type lookup used by badge components (no community context).
export const getVerificationType = (verifiedMap, uid, email) => {
    if (uid && verifiedMap[uid] && verifiedMap[uid].verified) {
        return verifiedMap[uid].verificationType === "gold" ? "gold" : "blue";
    }
    if (email && BOOTSTRAP_VERIFIED_EMAILS.includes(email.toLowerCase())) return "blue";
    return null;
};

export const BADGE_TOOLTIPS = {
    blue: "Verified by Fellora",
    gold: "Official member",
    purple: "Community Founder",
};

export const BADGE_COLORS = {
    blue: "#1DA1F2",
    gold: "#d4a017",
    purple: "#8e44ad",
};

// ─── Super Admin helpers ────────────────────────────────────────────────────
export const isSuperAdmin = async (uid) => {
    if (!uid) return false;
    try {
        const doc = await db.collection("users").doc(uid).get();
        return doc.exists && doc.data()?.role === "SUPER_ADMIN";
    } catch {
        return false;
    }
};

export const subscribeUserRoles = (cb) =>
    db.collection("users").onSnapshot(
        (snap) => {
            const map = {};
            snap.forEach((d) => (map[d.id] = { id: d.id, ...d.data() }));
            cb(map);
        },
        () => cb({})
    );

// Grant verification to a user. Writes both the verifiedUsers doc (drives global
// badge display) and the users doc (drives AdminPanel search results).
export const grantVerification = async (uid, badgeType, adminName, targetUser = {}) => {
    const type = badgeType === "gold" ? "gold" : "blue";
    const name = targetUser.displayName || targetUser.name || "Fellow";
    const doc = {
        uid,
        name,
        email: targetUser.email || "",
        photoURL: targetUser.photoURL || "",
        verified: true,
        verificationType: type,
        verifiedBy: adminName || "scrubsmedina",
        verifiedDate: new Date().toISOString(),
    };
    await db.collection(VERIFIED_COLL).doc(uid).set(doc);
    await db
        .collection("users")
        .doc(uid)
        .set({ verified: true, verificationType: type }, { merge: true });
    pushNotification(uid, {
        type: "info",
        fromName: "Fellora",
        message:
            type === "gold"
                ? "Your organization is now verified (Gold badge)."
                : "Your account has been verified by Fellora.",
    });
};

// Remove verification from a user. The badge disappears immediately everywhere.
export const revokeVerification = async (uid, adminName) => {
    await db
        .collection(VERIFIED_COLL)
        .doc(uid)
        .update({
            verified: false,
            verificationType: firebase.firestore.FieldValue.delete(),
            verifiedBy: adminName || "scrubsmedina",
            revokedDate: new Date().toISOString(),
        })
        .catch(() => {
            return db.collection(VERIFIED_COLL).doc(uid).set({ verified: false });
        });
    await db
        .collection("users")
        .doc(uid)
        .update({ verified: false, verificationType: firebase.firestore.FieldValue.delete() })
        .catch(() => {});
    pushNotification(uid, {
        type: "info",
        fromName: "Fellora",
        message: "Your verified badge has been removed.",
    });
};

// Simple setter (old API that grants/revokes without target payload).
export const setVerification = async (target, verified, type, actor) => {
    await grantVerification(
        target.uid,
        verified ? type || "blue" : "blue",
        actor?.email || target.email || "scrubsmedina",
        verified
            ? { displayName: target.name, email: target.email, photoURL: target.photoURL }
            : {}
    );
    if (!verified) await revokeVerification(target.uid, actor?.email || "scrubsmedina");
};

// ─── Verification Requests ───────────────────────────────────────────────────
// Expected data: { category, requestedBadge, reason, documentUrl }.
export const submitVerificationRequest = async (
    user,
    { category = "Individual", requestedBadge = "blue", reason = "", documentUrl = "" } = {}
) => {
    const uid = user?.uid;
    if (!uid) throw new Error("No authenticated user.");
    const email = user?.email || "";
    await db.collection("verificationRequests").doc(uid).set({
        uid,
        userName: user?.displayName || user?.name || email,
        userEmail: email,
        userPhoto: user?.photoURL || "",
        category,
        requestedBadge,
        reason,
        documentUrl,
        status: "PENDING", // PENDING | APPROVED | REJECTED
        submittedAt: new Date().toISOString(),
    });
};

export const subscribeVerificationRequests = (cb) =>
    db.collection("verificationRequests").onSnapshot(
        (snap) => {
            const list = [];
            snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
            cb(list);
        },
        () => cb([])
    );

// Process a request. status: APPROVED | REJECTED. Grants verification when approved.
export const processVerificationRequest = async (
    reqId,
    reqUid,
    status,
    badgeType,
    adminName,
    userInfo = {}
) => {
    const statusNormalized = ["APPROVED", "REJECTED"].includes(status) ? status : "REJECTED";
    if (statusNormalized === "APPROVED") {
        await grantVerification(reqUid, badgeType || "blue", adminName, {
            displayName: userInfo.displayName,
            email: userInfo.email,
            photoURL: userInfo.photoURL,
        });
    } else {
        pushNotification(reqUid, {
            type: "info",
            fromName: "Fellora",
            message: "Your verification request was rejected.",
        });
    }
    await db
        .collection("verificationRequests")
        .doc(reqId)
        .update({ status: statusNormalized, decidedAt: new Date().toISOString() })
        .catch(() => {});
};

// Simple decider (old API, kept for compatibility).
export const decideVerificationRequest = async (req, status, actor) => {
    await db
        .collection("verificationRequests")
        .doc(req.uid)
        .update({
            status: status === "approved" ? "APPROVED" : "REJECTED",
            decidedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
    pushNotification(req.uid, {
        type: "info",
        fromUid: actor?.uid || "",
        fromName: actor?.displayName || "Fellora",
        message:
            status === "approved"
                ? "Your verification request was approved."
                : "Your verification request was rejected.",
    });
};